import React from 'react';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import {
    EditorSendTestData,
    FileType,
    EditorToolbarCustomFormatActionProps,
    EditorShowSpecialFieldsEventProps,
} from '@dev/template-editor';
import { Link } from '@material-ui/core';

import { ShowDialog } from '@/store/dialog/actions';
import { UploadEditorFile, UploadEditorFileSuccess } from '@/store/templateEditor/actions';
import { links } from '@/services/dictionaries/links';
import { isCustomBranding } from '@/services/Secuirty';
import { EditorType } from '@/services/dictionaries/EditorType';
import { useGetSendTestModalParams } from '../../../../components/TemplateEditor/hooks/useShowSendTestModalAction';
import { useEditTagsModalParams } from '../../../../components/TemplateEditor/hooks/useEditTagsModalParams';
import { useDefaultOptionsModalParams } from '../../../../components/TemplateEditor/hooks/useDefaultOptionsModalParams';
import { useEditBodyPartModalParams } from '../../../../components/TemplateEditor/hooks/useEditBodyPartModalParams';
import { TemplateEditorHookActions } from '../../../../components/TemplateEditor/TemplateEditorInterface';
import { useSpecialFieldsModalParams } from '../../../../components/TemplateEditor/hooks/useSpecialFieldsModalParams';
import { useAiTextModalParams } from '../useAiTextModalParams';
import { useGetDisposableAITextToolModalParams } from '../../../../components/TemplateEditor/hooks/useDisposableAITextToolModalParams';

export const useTemplateEditorActions = (editorType?: EditorType): TemplateEditorHookActions => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { getEditTagsModalParams } = useEditTagsModalParams();
    const { getDefaultOptionsModalParams } = useDefaultOptionsModalParams();
    const { getEditBodyPartModalParams } = useEditBodyPartModalParams();
    const { getSpecialFieldsModalParams } = useSpecialFieldsModalParams();
    const { getSendTestModalParams } = useGetSendTestModalParams();
    const { getAiTextModalProps } = useAiTextModalParams(editorType);
    const { getDisposableAITextToolModalParams, displayAiTextToolModal } = useGetDisposableAITextToolModalParams();

    const showSendTestModal = (data: EditorSendTestData): void => {
        dispatch(ShowDialog(getSendTestModalParams(data)));
    };

    const showOnceAITextToolModal = (): void => {
        if (displayAiTextToolModal) {
            dispatch(ShowDialog(getDisposableAITextToolModalParams()));
        }
    };

    const showEditTextBodyModal = (): void => {
        dispatch(
            ShowDialog(
                getEditBodyPartModalParams({
                    contentType: 'text/plain',
                    title: t('editbodytext'),
                    alert: t('bodytextmessage'),
                    label: t('bodytext'),
                })
            )
        );
    };

    const showEditAmpBodyModal = (): void => {
        const howToAmpMessage = (
            <Trans
                i18nKey="learnmoreabouthowtouseamp"
                components={{ url: <Link href={links.howToCreateAmpEmails} target="_blank" /> }}
            />
        );
        const alert = (
            <>
                {t('bodyampmessage')} {!isCustomBranding() && howToAmpMessage}
            </>
        );
        dispatch(
            ShowDialog(
                getEditBodyPartModalParams({
                    contentType: 'text/x-amp-html',
                    title: t('editbodyamp'),
                    alert,
                    label: t('bodyamp'),
                })
            )
        );
    };

    const showDefaultOptionsModal = (): void => {
        dispatch(ShowDialog(getDefaultOptionsModalParams()));
    };
    const showEditTagsModal = (): void => {
        dispatch(ShowDialog(getEditTagsModalParams()));
    };

    const showSpecialFieldsModal = (props: EditorShowSpecialFieldsEventProps): void => {
        dispatch(ShowDialog(getSpecialFieldsModalParams(props)));
    };

    const uploadFile = (file: File, blockId: string): void => {
        dispatch(UploadEditorFile(file, blockId));
    };

    const editorFilesUpdate = (files: FileType[]): void => {
        dispatch(UploadEditorFileSuccess(files));
    };

    const showAiTextToolModal = (props: EditorToolbarCustomFormatActionProps): void => {
        dispatch(ShowDialog(getAiTextModalProps(props)));
    };

    return {
        showSendTestModal,
        showEditTextBodyModal,
        showEditAmpBodyModal,
        showDefaultOptionsModal,
        showEditTagsModal,
        showSpecialFieldsModal,
        uploadFile,
        editorFilesUpdate,
        showAiTextToolModal,
        showOnceAITextToolModal,
    };
};
